import { head, unless, and, is, length, of, pipe, flip, gt } from 'ramda'

import type { Ref, DeepReadonly, ToRefs } from 'vue'
import { toRefs, readonly, watch, onUnmounted, reactive } from 'vue'

interface State {
  error: Error | null
  file: File | null | undefined
}

interface Params {
  originalFile: Ref<File | null | undefined>
  fileInput: Ref<HTMLInputElement | null>
  onFileUploaded(file: File): void
}

export interface UseFileUploader extends DeepReadonly<ToRefs<State>> {
  removeOldFile(): void
  uploadNewFile(): void
  getFileAcceptedType(name: string | null | undefined): string
}

export function useFileUploader({ originalFile, fileInput, onFileUploaded }: Params): UseFileUploader {
  const state = reactive<State>({
    error: null,
    file: null,
  })

  watch(
    originalFile,
    (originalFile: File | null | undefined) => {
      state.file = originalFile
    },
    { immediate: true }
  )

  watch(fileInput, (fileInput: HTMLInputElement | null) => {
    fileInput?.addEventListener('change', handleFileInputChange)
  })

  onUnmounted(() => {
    fileInput.value?.removeEventListener('change', handleFileInputChange)
  })

  function uploadNewFile() {
    fileInput.value?.click()
  }

  function removeOldFile() {
    state.file = null
  }

  function getFileAcceptedType(name: string | null | undefined): string {
    if (!name) return '*'

    switch (name) {
      case 'pdfs':
        return '.pdf'
      case 'videos':
        return 'video/*'
      default:
        return 'image/*'
    }
  }

  function handleFileInputChange() {
    try {
      const file = _getFile(fileInput.value?.files)

      if (!file) return

      onFileUploaded(file)
    } catch (e) {
      state.error = e instanceof Error ? new Error(e.message) : (e as any)
    }

    _clearFileInput()
  }

  function _getFile(files: FileList | undefined | null): File | undefined | null {
    const hasLength = pipe(length, flip(gt)(0))
    const isArrayAndHasLength = and(is(Array), hasLength)

    return pipe<any[], any[], File | undefined | null>(unless(isArrayAndHasLength, of), head)(files)
  }

  function _clearFileInput() {
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }

  return {
    ...toRefs(readonly(state)),
    removeOldFile,
    uploadNewFile,
    getFileAcceptedType,
  }
}
